import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { COGNITO_KEYS } from '@uptime/shared/constants';
import { COGNITO_CLIENT_ID_HEADER } from '@uptime/shared/constants/cognito';
import { getAuthenticationHeader } from '@uptime/shared/utils/general';

const errorLink = onError(({ networkError, graphQLErrors, protocolErrors }) => {
  if (networkError) {
    // trackExpiredSession(networkError);
    console.error(`[Network error]: ${networkError}`);
  }

  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}.`)
    );
  }

  if (protocolErrors) {
    protocolErrors.forEach(({ message, extensions }) => {
      console.error(`[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`);
    });
  }
});

const filesLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FILE,
});

const devicesLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_DEVICE,
});

const newDevicesLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_NEW_DEVICE,
});

const userLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_USER,
});

const facilityLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FACILITY,
});

const goCheckItLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_GOCHECKIT,
});

const marketplaceLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_MARKETPLACE,
});

const flowLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_FLOW,
});

const iotLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_IOT,
});

const pdfGeneratorLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_PDF_GENERATOR,
});

const authLink = setContext((GraphQLRequest, { headers }) => {
  const accessHeader = getAuthenticationHeader();
  const usedClientId = localStorage.getItem(COGNITO_KEYS.CLIENT_ID);
  const clientHeader = usedClientId ? { [COGNITO_CLIENT_ID_HEADER]: usedClientId } : {};

  return {
    headers: {
      ...headers,
      ...clientHeader,
      ...accessHeader,
    },
  };
});

const cache = new InMemoryCache();
const cacheNoTypeName = new InMemoryCache({ addTypename: false });
const cacheForFlows = new InMemoryCache({
  typePolicies: {
    WorkOrderItem: {
      keyFields: ['workOrderId'],
    },
  },
});

const isDevToolsEnabled = !import.meta.env.PROD;

export const userClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(userLink)]),
  cache: cacheNoTypeName,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'user',
  },
});

export const filesClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(filesLink)]),
  cache: cacheNoTypeName,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'files',
  },
});

export const devicesClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(devicesLink)]),
  cache: cacheNoTypeName,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'devices',
  },
});

export const newDeviceClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(newDevicesLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'newDevice',
  },
});

export const facilityClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(facilityLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'facility',
  },
});

export const goCheckItClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(goCheckItLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'goCheckIt',
  },
});

export const marketplaceClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(marketplaceLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'marketplace',
  },
});

export const flowClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(flowLink)]),
  cache: cacheForFlows,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'flow',
  },
});

export const iotClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(iotLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'iot',
  },
});

export const pdfGeneratorClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(pdfGeneratorLink)]),
  cache,
  devtools: {
    enabled: isDevToolsEnabled,
    name: 'pdfGenerator',
  },
});

if (isDevToolsEnabled) {
  loadDevMessages();
  loadErrorMessages();
}
